import React, { useState, useEffect, useMemo } from 'react';
import API from '../api';

/*
 * Shared exam-paper UI for Admin ("Exam Papers (All Companions)") and the
 * Companion's "Exam Papers" tab: student picker, search, group-by, date
 * filter, and per-paper students + sessions shown inline under each paper.
 * Backend: app/routes/exams.py
 */

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x.msg).join('; ');
  return d || fallback;
};
const fmtDay = s => (s ? new Date(s.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDT = s => (s ? new Date(s.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z')).toLocaleString('en-GB') : '—');
const monthLabel = ym => { const [y, m] = ym.split('-'); return new Date(+y, +m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }); };
const pill = (bg, fg) => ({ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: bg, color: fg, whiteSpace: 'nowrap' });

const STATUS = {
  completed:     { bg: '#dcfce7', fg: '#15803d', t: '✓ Attended' },
  in_progress:   { bg: '#fef9c3', fg: '#a16207', t: '⏳ In progress' },
  not_attempted: { bg: '#fee2e2', fg: '#b91c1c', t: '✕ Not attempted' },
};

// ── Student multi-select (only students this user may assign) ──────────────
export function StudentPicker({ value, onChange }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    API.get('/exams/assignable-students').then(r => setAll(r.data || [])).catch(() => setAll([]));
  }, []);
  const selected = new Set(value);
  const toggle = id => onChange(selected.has(id) ? value.filter(x => x !== id) : [...value, id]);
  const shown = (all || []).filter(s => !q || s.name.toLowerCase().includes(q.toLowerCase()));
  const names = (all || []).filter(s => selected.has(s.id));

  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 10, background: '#fff' }}>
      {names.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {names.map(s => (
            <span key={s.id} style={{ ...pill('#eff6ff', '#1e40af'), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {s.name}
              <button type="button" onClick={() => toggle(s.id)} aria-label={`Remove ${s.name}`}
                style={{ border: 'none', background: 'none', color: '#1e40af', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students to add…"
        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 170, overflowY: 'auto', marginTop: 6 }}>
        {all === null ? <div style={{ padding: 8, color: '#94a3b8', fontSize: 12 }}>Loading…</div>
          : shown.length === 0 ? <div style={{ padding: 8, color: '#94a3b8', fontSize: 12 }}>No students found</div>
          : shown.map(s => (
            <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              {s.class_name && <span style={{ color: '#94a3b8', fontSize: 12 }}>{s.class_name}</span>}
            </label>
          ))}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
        {value.length} selected · Only these students (and students whose test uses this paper) will see it under Attend Exam.
      </div>
    </div>
  );
}

// ── Edit exam date + students ──────────────────────────────────────────────
function EditDetailsModal({ paper, onClose, onSaved }) {
  const [examDate, setExamDate] = useState(paper.exam_date_source === 'paper' ? paper.exam_date : '');
  // Only students picked on the paper; ones linked through a scheduled test stay linked automatically.
  const [ids, setIds] = useState(paper.students.filter(s => s.explicit).map(s => s.student_id));
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await API.patch(`/exams/${paper.id}/details`, examDate ? { exam_date: examDate, student_ids: ids } : { clear_exam_date: true, student_ids: ids });
      onSaved();
    } catch (e) { setMsg(errMsg(e, 'Could not save')); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 22, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#0f1f45' }}>✏️ {paper.title}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: '#f1f5f9', cursor: 'pointer' }}>×</button>
        </div>
        {msg && <div className="alert alert-error" onClick={() => setMsg('')}>{msg}</div>}
        <div className="form-group">
          <label>Exam date</label>
          <input className="form-control" type="date" value={examDate} onChange={e => setExamDate(e.target.value)} />
          <small style={{ color: '#94a3b8' }}>
            {paper.exam_date_source === 'test' && !examDate
              ? `Leave empty to use the scheduled test date (${fmtDay(paper.exam_date)}).`
              : 'Optional when the paper is linked to a scheduled test — the test date is used.'}
          </small>
        </div>
        <div className="form-group">
          <label>Students this paper is for</label>
          <StudentPicker value={ids} onChange={setIds} />
          {paper.students.some(s => s.assigned && !s.explicit) && (
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6 }}>
              Also linked through a scheduled test (always included): {paper.students.filter(s => s.assigned && !s.explicit).map(s => s.name).join(', ')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Students + sessions, shown inline under a paper ────────────────────────
function PaperDetails({ paper }) {
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState('');
  const [openFlags, setOpenFlags] = useState(null);
  useEffect(() => {
    API.get(`/exams/${paper.id}/sessions`).then(r => setSessions(r.data || [])).catch(e => setErr(errMsg(e, 'Could not load sessions')));
  }, [paper.id, paper.session_count]);

  const notAttempted = paper.students.filter(s => s.status === 'not_attempted');
  return (
    <div style={{ marginTop: 12, borderTop: '1px dashed #e2e8f0', paddingTop: 12 }}>
      {notAttempted.length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 12.5, color: '#b91c1c' }}>
          <b>Not attempted yet:</b> {notAttempted.map(s => s.name).join(', ')}
        </div>
      )}
      {err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      {sessions === null ? !err && <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading sessions…</div>
        : sessions.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 12 }}>No one has attended this exam yet.</div>
        : sessions.map(s => (
          <div key={s.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#0f1f45' }}>{s.student_name}</span>
              <span style={s.completed_at || s.time_over ? pill('#dcfce7', '#15803d') : pill('#fef9c3', '#a16207')}>
                {s.completed_at ? 'Completed' : s.time_over ? 'Time over' : 'In progress'}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Started {fmtDT(s.started_at)}{s.completed_at ? ` · Ended ${fmtDT(s.completed_at)}` : ''}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: s.flag_count > 5 ? '#dc2626' : s.flag_count > 0 ? '#d97706' : '#16a34a' }}>
                {s.flag_count} flag{s.flag_count !== 1 ? 's' : ''}
              </span>
              {s.flag_count > 0 && (
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 10px' }} onClick={() => setOpenFlags(openFlags === s.id ? null : s.id)}>
                  {openFlags === s.id ? 'Hide flags' : 'View flags'}
                </button>
              )}
            </div>
            {openFlags === s.id && (
              <div style={{ marginTop: 6, background: '#fef2f2', borderRadius: 8, padding: 8, maxHeight: 200, overflowY: 'auto' }}>
                {s.flags.map((f, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#7f1d1d', padding: '2px 0' }}>
                    ⚑ <b>{f.reason}</b> <span style={{ color: '#94a3b8', fontSize: 11 }}>{fmtDT(f.ts)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ── One paper card ─────────────────────────────────────────────────────────
function PaperCard({ paper, showTutor, open, onToggleOpen, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggleActive = async () => {
    setBusy(true);
    try { await API.patch(`/exams/${paper.id}/toggle`); onChanged(); }
    catch (e) { alert(errMsg(e, 'Failed to change status')); }
    finally { setBusy(false); }
  };
  const viewPdf = async () => {
    const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    try {
      const r = await API.get(`/exams/${paper.id}/pdf-access`);
      const pt = r.data?.pdf_token;
      if (!pt) throw new Error('no token');
      window.open(`${base}/exams/${paper.id}/pdf?pdf_token=${encodeURIComponent(pt)}`, '_blank', 'noopener,noreferrer');
    } catch (_) { alert('Could not open PDF.'); }
  };

  return (
    <div style={{ background: '#fff', border: `1px solid ${open ? '#2554b8' : '#e2e8f0'}`, borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5, color: '#0f1f45' }}>{paper.title}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
            📅 {paper.exam_date
              ? <>{fmtDay(paper.exam_date)}{paper.exam_date_source === 'test' && <span style={{ color: '#94a3b8' }}> (test date)</span>}</>
              : <span title="No exam date and no scheduled test — sorted by the day it was created">Created {fmtDay(paper.sort_date)}</span>}
            {paper.subject && <> · {paper.subject}</>}
            {showTutor && <> · 👨‍🏫 {paper.tutor_name}</>}
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
            Code: <span style={{ fontWeight: 700, color: '#2554b8', fontFamily: 'monospace' }}>{paper.secret_code}</span>
            {' · '}{paper.duration_minutes} min · {paper.session_count} attempt{paper.session_count !== 1 ? 's' : ''} · {paper.total_flags} flag{paper.total_flags !== 1 ? 's' : ''}
          </div>
          {paper.assigned_count === 0 && (
            <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12, fontWeight: 600 }}>
              ⚠ No students assigned — students can't see this exam until you add them with ✏️ Edit (or link it to their test).
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>👥</span>
            {paper.students.length === 0
              ? <span style={{ fontSize: 12, color: '#94a3b8' }}>No students assigned or attended yet</span>
              : paper.students.map(s => {
                const st = STATUS[s.status];
                return (
                  <span key={s.student_id} style={pill(st.bg, st.fg)} title={`${st.t}${s.assigned ? (s.explicit ? '' : ' · linked by test') : ' · no longer assigned'}${s.flag_count ? ` · ${s.flag_count} flags` : ''}`}>
                    {s.name}{!s.assigned && ' *'}{s.flag_count > 0 && ` · ⚑${s.flag_count}`}
                  </span>
                );
              })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={paper.is_active ? pill('#dcfce7', '#16a34a') : pill('#fee2e2', '#dc2626')}>{paper.is_active ? 'Active' : 'Inactive'}</span>
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={toggleActive} disabled={busy}>
            {paper.is_active ? 'Deactivate' : 'Activate'}
          </button>
          {paper.has_pdf && <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={viewPdf}>View PDF</button>}
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={onEdit}>✏️ Edit</button>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={onToggleOpen}>
            {open ? 'Hide sessions ▲' : 'Sessions ▼'}
          </button>
        </div>
      </div>
      {open && <PaperDetails paper={paper} />}
    </div>
  );
}

// ── Search + group + filter + list ─────────────────────────────────────────
export function ExamPaperBrowser({ papers, showTutor = false, onChanged }) {
  const [q, setQ] = useState('');
  const [groupBy, setGroupBy] = useState('date');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (papers || [])
      .filter(p => {
        if (from && (p.sort_date || '') < from) return false;
        if (to && (p.sort_date || '') > to) return false;
        if (!needle) return true;
        const hay = [p.title, p.subject, p.secret_code, showTutor ? p.tutor_name : '', ...p.students.map(s => s.name)]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(needle);
      })
      .sort((a, b) => (b.sort_date || '').localeCompare(a.sort_date || '') || b.id - a.id);
  }, [papers, q, from, to, showTutor]);

  const groups = useMemo(() => {
    const g = new Map();
    for (const p of filtered) {
      const key = groupBy === 'tutor' ? (p.tutor_name || `Companion #${p.tutor_id}`) : (p.sort_date || '').slice(0, 7) || 'unknown';
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(p);
    }
    const entries = [...g.entries()];
    if (groupBy === 'tutor') entries.sort((a, b) => a[0].localeCompare(b[0]));
    return entries;
  }, [filtered, groupBy]);

  const anyFilter = q || from || to;
  const total = (papers || []).length;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '12px 14px', background: '#f8faff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 16 }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={showTutor ? 'Search paper, code, student or companion…' : 'Search paper, code or student…'}
          style={{ flex: '1 1 240px', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
          Exam date
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12 }} />
          to
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12 }} />
        </label>
        {showTutor && (
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 700 }}>
            <option value="date">Group by exam date</option>
            <option value="tutor">Group by companion</option>
          </select>
        )}
        {anyFilter && (
          <button onClick={() => { setQ(''); setFrom(''); setTo(''); }} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: '#e0e7ff', color: '#2554b8', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Clear</button>
        )}
        <span style={{ fontSize: 12, color: '#64748b', marginLeft: 'auto' }}>
          {anyFilter ? `${filtered.length} of ${total}` : total} paper{total !== 1 ? 's' : ''}
        </span>
      </div>

      {groups.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
          {total === 0 ? 'No exam papers uploaded yet.' : 'No papers match your search.'}
        </div>
      )}

      {groups.map(([key, list]) => (
        <div key={key} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#2554b8', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            {groupBy === 'tutor' ? <>👨‍🏫 {key}</> : <>📅 {key === 'unknown' ? 'No date' : monthLabel(key)}</>}
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>({list.length} paper{list.length !== 1 ? 's' : ''})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {list.map(p => (
              <PaperCard key={p.id} paper={p} showTutor={showTutor && groupBy === 'date'}
                open={openId === p.id} onToggleOpen={() => setOpenId(openId === p.id ? null : p.id)}
                onEdit={() => setEditing(p)} onChanged={onChanged} />
            ))}
          </div>
        </div>
      ))}

      <div style={{ fontSize: 11, color: '#94a3b8' }}>
        Student colours: <span style={pill(STATUS.completed.bg, STATUS.completed.fg)}>attended</span>{' '}
        <span style={pill(STATUS.in_progress.bg, STATUS.in_progress.fg)}>in progress</span>{' '}
        <span style={pill(STATUS.not_attempted.bg, STATUS.not_attempted.fg)}>not attempted</span> · * = attended earlier, no longer assigned
      </div>

      {editing && <EditDetailsModal paper={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </div>
  );
}
